import { create } from 'zustand';
import { ChatMessage, Conversation, ConversationType, Negotiation, OnlineStatus, WSEventType, WSMessage } from '../types';
import { chatService } from '../services/chatService';
import { chatWS } from '../services/websocket';
import { useAuthStore } from './authStore';

interface ChatStore {
  // ─── Negotiation chat (legacy) ─────────────────────────
  messages: ChatMessage[];
  negotiation: Negotiation | null;
  isLoading: boolean;
  isSending: boolean;
  error: string | null;
  isConnected: boolean;
  isTyping: boolean;
  partnerPresence: { status: OnlineStatus; lastSeenAt?: string } | null;
  hasMore: boolean;
  page: number;

  loadNegotiation: (negotiationId: string) => Promise<void>;
  loadMessages: (negotiationId: string, page?: number) => Promise<void>;
  loadMore: (negotiationId: string) => Promise<void>;
  sendMessage: (negotiationId: string, content: string, replyToId?: string) => Promise<void>;
  sendTyping: () => void;
  markMessagesRead: (negotiationId: string) => Promise<void>;
  connectWS: (negotiationId: string) => Promise<void>;
  disconnectWS: () => void;
  addMessage: (message: ChatMessage) => void;
  clearChat: () => void;

  // ─── Inbox / conversations ─────────────────────────────
  conversations: Conversation[];
  conversationsLoading: boolean;
  conversationsError: string | null;
  activeConversation: Conversation | null;
  conversationMessages: ChatMessage[];
  conversationMessagesLoading: boolean;

  loadConversations: (type?: ConversationType) => Promise<void>;
  loadConversation: (conversationId: string) => Promise<void>;
  loadConversationMessages: (conversationId: string) => Promise<void>;
  sendConversationMessage: (conversationId: string, content: string, replyToId?: string) => Promise<void>;
  blockConversation: (conversationId: string, reason: string) => Promise<void>;
  unblockConversation: (conversationId: string) => Promise<void>;
  removeMessage: (conversationId: string, messageId: string) => Promise<void>;
  clearConversation: () => void;
}

export const useChatStore = create<ChatStore>((set, get) => ({
  // ─── Negotiation chat state ────────────────────────────
  messages: [],
  negotiation: null,
  isLoading: false,
  isSending: false,
  error: null,
  isConnected: false,
  isTyping: false,
  partnerPresence: null,
  hasMore: true,
  page: 1,

  // ─── Inbox / conversations state ───────────────────────
  conversations: [],
  conversationsLoading: false,
  conversationsError: null,
  activeConversation: null,
  conversationMessages: [],
  conversationMessagesLoading: false,

  loadNegotiation: async (negotiationId: string) => {
    try {
      set({ isLoading: true, error: null });
      const data = await chatService.getNegotiation(negotiationId);
      const partner = data?.surveyor || data?.client;
      set({
        negotiation: data,
        partnerPresence: partner
          ? {
              status: partner.online_status || 'offline',
              lastSeenAt: partner.last_seen_at || partner.updated_at,
            }
          : null,
      });
    } catch (err: any) {
      set({ error: err?.message || 'Failed to load negotiation' });
    } finally {
      set({ isLoading: false });
    }
  },

  loadMessages: async (negotiationId: string, page = 1) => {
    try {
      set({ isLoading: true, error: null });
      const data = await chatService.getHistory(negotiationId, { page, page_size: 50 });
      set({
        messages: page === 1 ? (data.results ?? []).reverse() : [...(data.results ?? []).reverse(), ...get().messages],
        hasMore: data.next !== null,
        page,
      });
    } catch (err: any) {
      set({ error: err?.message || 'Failed to load messages' });
    } finally {
      set({ isLoading: false });
    }
  },

  loadMore: async (negotiationId: string) => {
    const { hasMore, page, isLoading } = get();
    if (!hasMore || isLoading) return;
    await get().loadMessages(negotiationId, page + 1);
  },

  sendMessage: async (negotiationId: string, content: string, replyToId?: string) => {
    try {
      set({ isSending: true, error: null });
      // Try WS first, fallback to HTTP
      if (chatWS.isConnected) {
        chatWS.sendChatMessage(content, replyToId);
      } else {
        const msg = await chatService.sendMessage({
          negotiation_id: negotiationId,
          content,
          reply_to: replyToId,
        });
        get().addMessage(msg);
      }
    } catch (err: any) {
      set({ error: err?.message || 'Failed to send message' });
    } finally {
      set({ isSending: false });
    }
  },

  sendTyping: () => {
    if (chatWS.isConnected) {
      chatWS.sendTyping();
    }
  },

  markMessagesRead: async (negotiationId: string) => {
    const unreadIds = get()
      .messages.filter((m) => !m.read_at)
      .map((m) => m.id);

    if (chatWS.isConnected && unreadIds.length > 0) {
      chatWS.sendReadReceipt(unreadIds);
    }

    try {
      await chatService.markNegotiationRead(negotiationId);
    } catch {
      // Backends without read receipts can ignore this optimistic UI hook.
    }
  },

  connectWS: async (negotiationId: string) => {
    await chatWS.connect(negotiationId);

    chatWS.on('connection', (msg) => {
      set({ isConnected: msg.payload.status === 'connected' });
    });

    chatWS.on(WSEventType.CHAT_MESSAGE, (msg: WSMessage) => {
      const chatMsg = msg.payload as ChatMessage;
      get().addMessage(chatMsg);
    });

    chatWS.on(WSEventType.MESSAGE_BLOCKED, (msg: WSMessage) => {
      const blockedMsg = msg.payload as ChatMessage;
      get().addMessage({ ...blockedMsg, is_blocked: true });
    });

    chatWS.on(WSEventType.TYPING, (msg: WSMessage) => {
      if (msg.payload?.user_id === useAuthStore.getState().user?.id) return;
      set({ isTyping: true });
      setTimeout(() => set({ isTyping: false }), 3000);
    });

    chatWS.on(WSEventType.PRESENCE, (msg: WSMessage) => {
      if (msg.payload?.user_id === useAuthStore.getState().user?.id) return;
      const payload = msg.payload as { status?: OnlineStatus; last_seen_at?: string; lastSeenAt?: string };
      set({
        partnerPresence: {
          status: payload.status || 'offline',
          lastSeenAt: payload.last_seen_at || payload.lastSeenAt,
        },
      });
    });

    chatWS.on(WSEventType.MESSAGE_READ, (msg: WSMessage) => {
      const payload = msg.payload as { message_ids?: string[]; read_at?: string; reader_id?: string };
      const readAt = payload.read_at || new Date().toISOString();
      set((state) => ({
        messages: state.messages.map((message) =>
          !payload.message_ids || payload.message_ids.includes(message.id)
            ? { ...message, read_at: message.read_at || readAt }
            : message,
        ),
      }));
    });

    chatWS.on('room_closed' as any, (msg: WSMessage) => {
      const reason = msg.payload?.reason || 'This chat has been closed.';
      set((state) => ({
        negotiation: state.negotiation
          ? { ...state.negotiation, is_chat_blocked: true, blocked_reason: reason }
          : state.negotiation,
        activeConversation: state.activeConversation
          ? { ...state.activeConversation, is_blocked: true, blocked_reason: reason }
          : state.activeConversation,
      }));
    });
  },

  disconnectWS: () => {
    chatWS.disconnect();
    set({ isConnected: false });
  },

  addMessage: (message: ChatMessage) => {
    set((state) => {
      // Avoid duplicates
      if (state.messages.find((m) => m.id === message.id)) return state;
      return { messages: [...state.messages, message] };
    });
  },

  clearChat: () => {
    set({
      messages: [],
      negotiation: null,
      isConnected: false,
      isTyping: false,
      partnerPresence: null,
      hasMore: true,
      page: 1,
      error: null,
    });
  },

  // ─── Inbox / conversations methods ─────────────────────
  loadConversations: async (type?: ConversationType) => {
    try {
      set({ conversationsLoading: true, conversationsError: null });
      const data = await chatService.listConversations({ type });
      set({ conversations: data.results ?? [] });
    } catch (err: any) {
      set({ conversationsError: err?.message || 'Failed to load conversations' });
    } finally {
      set({ conversationsLoading: false });
    }
  },

  loadConversation: async (conversationId: string) => {
    try {
      set({ conversationsLoading: true, conversationsError: null });
      const data = await chatService.getConversation(conversationId);
      set({ activeConversation: data });
    } catch (err: any) {
      set({ conversationsError: err?.message || 'Failed to load conversation' });
    } finally {
      set({ conversationsLoading: false });
    }
  },

  loadConversationMessages: async (conversationId: string) => {
    try {
      set({ conversationMessagesLoading: true, conversationsError: null });
      const data = await chatService.getConversationMessages(conversationId);
      set({ conversationMessages: (data.results ?? []).reverse() });
      await chatService.markConversationRead(conversationId).catch(() => undefined);
    } catch (err: any) {
      set({ conversationsError: err?.message || 'Failed to load messages' });
    } finally {
      set({ conversationMessagesLoading: false });
    }
  },

  sendConversationMessage: async (conversationId: string, content: string, replyToId?: string) => {
    try {
      set({ isSending: true, conversationsError: null });
      const msg = await chatService.sendConversationMessage(conversationId, content, replyToId);
      set((state) => ({
        conversationMessages: [...state.conversationMessages, msg],
      }));
    } catch (err: any) {
      set({ conversationsError: err?.message || 'Failed to send message' });
    } finally {
      set({ isSending: false });
    }
  },

  blockConversation: async (conversationId: string, reason: string) => {
    try {
      const updated = await chatService.blockConversation(conversationId, reason);
      set({ activeConversation: updated });
      // Update in list too
      set((state) => ({
        conversations: state.conversations.map((c) =>
          c.id === conversationId ? updated : c
        ),
      }));
    } catch (err: any) {
      set({ conversationsError: err?.message || 'Failed to block conversation' });
    }
  },

  unblockConversation: async (conversationId: string) => {
    try {
      const updated = await chatService.unblockConversation(conversationId);
      set({ activeConversation: updated });
      set((state) => ({
        conversations: state.conversations.map((c) =>
          c.id === conversationId ? updated : c
        ),
      }));
    } catch (err: any) {
      set({ conversationsError: err?.message || 'Failed to unblock conversation' });
    }
  },

  removeMessage: async (conversationId: string, messageId: string) => {
    try {
      await chatService.removeMessage(conversationId, messageId);
      set((state) => ({
        conversationMessages: state.conversationMessages.filter((m) => m.id !== messageId),
      }));
    } catch (err: any) {
      set({ conversationsError: err?.message || 'Failed to remove message' });
    }
  },

  clearConversation: () => {
    set({
      activeConversation: null,
      conversationMessages: [],
      conversationsError: null,
    });
  },
}));
